/**
 * Carteira de clientes: quantos clientes cada representante tem, para o
 * "% da carteira atendida" = clientes que compraram no mês ÷ clientes da carteira.
 *
 * Regras:
 * - A carteira é uma foto (o dia em que foi exportada). Vale a foto mais recente
 *   até o fim do mês; se não houver nenhuma até lá, a primeira depois (a tela
 *   mostra a data da foto usada).
 * - Só contam as situações de SITUACOES_DA_CARTEIRA (dominio/regras.ts).
 * - O código do vendedor vira representante como nas vendas, no mês do painel
 *   (dominio/identificacao.ts): o 34 do Luiz Eduardo conta para o Marcelo desde setembro.
 * - Só a contagem por código é guardada; nenhum dado de cliente.
 *
 * Lê: as carteiras e o identificador que recebe.
 * Usado por: dominio/painel.ts e telas/Importar.tsx (prévia da carteira).
 */
import { periodoDoMes } from './fechamento.ts';
import { normalizarNome } from './identificacao.ts';
import { SITUACOES_DA_CARTEIRA } from './regras.ts';
import type { Carteira, ContagemCarteira, Fonte, Mes } from './tipos.ts';

/** A foto da carteira que vale para o mês desta fonte (null = nenhuma importada). */
export function carteiraDoMes(carteiras: Carteira[], fonte: Fonte, mes: Mes): Carteira | null {
  const validas = carteiras
    .filter((carteira) => carteira.fonte === fonte && carteira.desfeitaEm === null)
    .sort((a, b) => a.dataReferencia.localeCompare(b.dataReferencia) || a.id - b.id);
  const { fim } = periodoDoMes(fonte, mes);
  const ateOFimDoMes = validas.filter((carteira) => carteira.dataReferencia <= fim);
  return ateOFimDoMes[ateOFimDoMes.length - 1] ?? validas[0] ?? null;
}

/** Esta situação cadastral conta na carteira? ("Normal" e "Novo" sim; "Bloqueado" e "Excluído" não.) */
export function contaNaCarteira(situacao: string): boolean {
  const alvo = normalizarNome(situacao);
  return SITUACOES_DA_CARTEIRA.some((aceita) => normalizarNome(aceita) === alvo);
}

/** Clientes da carteira (só as situações que contam) de cada representante, e quantos estão sem dono. */
export function clientesPorRepresentante(
  contagens: ContagemCarteira[],
  identificar: (codigo: string) => number | null,
): { porRepresentante: Map<number, number>; semDono: number } {
  const porRepresentante = new Map<number, number>();
  let semDono = 0;
  for (const contagem of contagens) {
    if (!contaNaCarteira(contagem.situacao)) continue;
    const id = contagem.codigoVendedor === '' ? null : identificar(contagem.codigoVendedor);
    if (id === null) semDono += contagem.quantidade;
    else porRepresentante.set(id, (porRepresentante.get(id) ?? 0) + contagem.quantidade);
  }
  return { porRepresentante, semDono };
}
